import { IGameState } from 'State/Game/IGameState';
import { GLayout } from 'Utility/layout/LayoutHelper';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import QuestHelper from 'Utility/QuestHelper';
import { MathHelper } from 'Utility/MathHelper';
import TransformComponent from 'Component/Transform';
import { progressBarFillMaxWidth } from 'Prototype/UI/QuestItem';
import TextComponent from 'Component/Render/Text';
import SpriteComponent from 'Component/Render/Sprite';
import Renderable from 'Component/Renderable';
import { IRewardData, getRewardData } from 'Utility/RewardHelper';
import Game from 'Game';
import { getFarmItemThumbnailAssetName, getFarmItemThumbnailPath } from 'System/Farm/FarmHelpers';
import LevelHelper from 'Utility/LevelHelper';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { Alpha } from 'System/Animation/Clip';
import CleanupManager from 'Utility/CleanupManager';
import { PlayerActions } from 'State/Player/PlayerActions';
import { isArray } from 'lodash';
import { report } from 'report';
import DateHelper from 'Utility/DateHelper';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { EasterEventQuestsPopupLayout } from 'Layouts/Events/easter_2024/questsMenu/EasterEventQuestsPopupLayout';
import { EventQuestsPopupLayout } from 'Layouts/Events/xmas_2021/questsMenu/EventQuestsPopupLayout';
import { Jul4EventQuestsPopupLayout } from 'Layouts/Events/jul4_2022/questsMenu/Jul4EventQuestsPopupLayout';
import { HalloweenEventQuestsPopupLayout } from 'Layouts/Events/halloween_2022/questsMenu/HalloweenEventQuestsPopupLayout';
import { Xmas2022EventQuestsPopupLayout } from 'Layouts/Events/xmas_2022/questsMenu/Xmas2022EventQuestsPopupLayout';
import { Xmas2023EventQuestsPopupLayout } from 'Layouts/Events/xmas_2023/questsMenu/Xmas2023EventQuestsPopupLayout';
import { FarmStateHelper } from 'State/Player/Farm/FarmStateHelper';
import { UIActions } from 'State/UI/UIActions';
import { PopupID } from 'State/UI/PopupID';

export function initButton(button: GameObject, onClick: () => unknown, cleanupManager: CleanupManager) {
    applyDefaultButtonAnimation(button);
    button.on(GameObjectEvent.SELECTED, onClick);
    cleanupManager.add(() => {
        button.off(GameObjectEvent.SELECTED, onClick);
    });
}

export function updateEventData(state: IGameState, layout: GLayout<unknown>): void {
    if (!isEventLayout(layout)) return;

    const eventQuest = EventStateHelper.getActiveQuest(state);
    const canCollect = QuestHelper.canCompleteQuest(eventQuest);

    layout.eventSubLayout.progressBarFill.getComponent(SpriteComponent).texture = canCollect
        ? 'mission_bar_green'
        : 'mission_bar_blue';
    layout.eventSubLayout.collectButton.getComponent(Renderable).visible = canCollect;

    // Check event quest complete.
    if (eventQuest.cooldownStartedAt !== null) {
        layout.eventSubLayout.lockSprite.getComponent(Renderable).visible = false;
        layout.eventSubLayout.bigCheckSprite.getComponent(Renderable).visible = true;
        layout.eventSubLayout.shadowSprite.getComponent(Renderable).visible = true;
    }
}

function onGiftClicked(layout: GLayout<EventQuestsPopupLayout>, rewards: IRewardData[]) {
    const state = Game.store.getState();

    const itemReward = rewards[0];
    const coinReward = rewards[1];

    // Item Reward
    const itemSprite = layout.eventSubLayout.itemSprite;
    const itemSpriteComponent = itemSprite.getComponent(SpriteComponent);
    itemSpriteComponent.texture = 'blank';
    let texture: string;
    if (typeof itemReward.value === 'string')
    {
        texture = getThumbnailTextureName(state, itemReward.value, itemReward.fallback);
        layout.eventSubLayout.itemText.getComponent(TextComponent).text = '';
        if (!Game.resource.existsOrQueued(texture)) {
            Game.resource.add(texture, getFarmItemThumbnailPath(itemReward.value));
        }
    }
    else
    {
        texture = itemReward.iconCacheKey;
        layout.eventSubLayout.itemText.getComponent(TextComponent).text = `x${itemReward.value}`;
    }

    Game.resource.wait(texture).then(() => {
        itemSpriteComponent.texture = texture;
    });

    const levelCost = LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState));

    layout.eventSubLayout.rewardText.getComponent(TextComponent).text = `${Math.round(coinReward.value * levelCost)}`;
    layout.eventSubLayout.rewardSprite.getComponent(SpriteComponent).texture = coinReward.iconCacheKey;

    const overlay = layout.eventSubLayout.overlay;
    const giftDetailsSprite = layout.eventSubLayout.giftDetailsSprite;
    fadeIn(overlay, 0.6);
    fadeIn(giftDetailsSprite);

    Game.ticker.setTimeout(() => {
        overlay.once(GameObjectEvent.SELECTED, () => {
            fadeOut(overlay, 0.6);
            fadeOut(giftDetailsSprite);
        });
    }, 500);
}

function getThumbnailTextureName(state: IGameState, itemId: string, fallback?: IRewardData): string {
    if (FarmStateHelper.alreadyHasItem(state.playerState, itemId) && fallback)
        return getRewardData(fallback.type).texture;
    return getFarmItemThumbnailAssetName(itemId);
}

function fadeIn(gameObject: GameObject, endAlpha: number = 1) {
    StartAnimationClip(
        new Alpha(gameObject, {
            startAlpha: 0,
            endAlpha,
            duration: 450,
        })
    );
}

function fadeOut(gameObject: GameObject, startAlpha: number = 1) {
    StartAnimationClip(
        new Alpha(gameObject, {
            startAlpha,
            endAlpha: 0,
            duration: 450,
        }).once(AnimationClipEvent.DESTROY, () => {
            gameObject.getComponent(Renderable).visible = false;
        })
    );
}

export function setEventQuestProgress(state: IGameState, layout: GLayout<unknown>) {
    if (!isEventLayout(layout)) return;

    const fill = layout.eventSubLayout.progressBarFill;
    const text = layout.eventSubLayout.progressText;
    const eventQuest = EventStateHelper.getActiveQuest(state);
    const target = QuestHelper.getFinalQuestTarget(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );

    // Set bar fill length
    if (eventQuest.progress !== 0) {
        const percProgress = MathHelper.normalize(eventQuest.progress, 0, target);
        fill.getComponent(TransformComponent).width = Math.min(
            MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
            progressBarFillMaxWidth
        );
    } else {
        fill.getComponent(TransformComponent).width = 0;
    }
    let currentProgress = MathHelper.clamp(eventQuest.progress, 0, target);
    // Set Progress text
    text.getComponent(TextComponent).text = `${currentProgress} / ${target}`;
}
type EventQuestLayout =
    | EventQuestsPopupLayout
    | EasterEventQuestsPopupLayout
    | Jul4EventQuestsPopupLayout
    | HalloweenEventQuestsPopupLayout
    | Xmas2023EventQuestsPopupLayout;

function isEventLayout(layout: any): layout is GLayout<EventQuestLayout> {
    return (
        layout instanceof EventQuestsPopupLayout ||
        layout instanceof EasterEventQuestsPopupLayout ||
        layout instanceof Jul4EventQuestsPopupLayout ||
        layout instanceof HalloweenEventQuestsPopupLayout ||
        layout instanceof Xmas2022EventQuestsPopupLayout ||
        layout instanceof Xmas2023EventQuestsPopupLayout
    );
}

export function createEventLayout(
    state: IGameState,
    layout: GLayout<EventQuestsPopupLayout>,
    cleanupManager: CleanupManager
) {
    if (!isEventLayout(layout)) return;

    const eventQuest = EventStateHelper.getActiveQuest(state);
    const finalTarget = QuestHelper.getFinalQuestTarget(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );
    const eventQuestTargets = QuestHelper.getQuestTarget(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );

    const button = layout.eventSubLayout.collectButton;

    initButton(button, () => PlayerActions.claimQuestRewardById(eventQuest.id), cleanupManager);

    const backgroundTexture = QuestHelper.getBackgroundTileKey(
        eventQuest.type,
        eventQuest.tier,
        QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
    );

    layout.eventSubLayout.eventBG.getComponent(SpriteComponent).texture = backgroundTexture;

    // Set quest title
    const questTitle = layout.eventSubLayout.missionText;
    const questTitleText = Game.locale.getText(QuestHelper.getTitleKey(eventQuest.type));
    questTitle.getComponent(TextComponent).text = questTitleText;

    // Set progress text
    const progressBarText = layout.eventSubLayout.progressText;
    const progressText = `${eventQuest.progress} / ${finalTarget}`;
    progressBarText.getComponent(TextComponent).text = progressText;

    updateEventData(state, layout);

    if (isArray(eventQuestTargets)) {
        layout.eventSubLayout.giftTooltipText.forEach((text: any, index: number) => {
            const currentTarget = eventQuestTargets[index];
            const giftText = layout.eventSubLayout.giftTooltipText[index];

            const giftTextComponent = giftText.getComponent(TextComponent);
            giftTextComponent.text = String(currentTarget);

            const parentTransform = giftText.parent.getComponent(TransformComponent);
            const percProgress = MathHelper.normalize(currentTarget, 0, finalTarget);

            parentTransform.x = Math.min(
                MathHelper.lerp(0, progressBarFillMaxWidth, percProgress),
                progressBarFillMaxWidth - 15
            );

            const rewards = QuestHelper.getQuestRewards(
                0,
                QuestHelper.getQuestPoolKeyByEventType(eventQuest.eventType)
            );
            if (!isArray(rewards[index])) {
                report('Multi target quest with mismatching rewards', {
                    once: true,
                    sampleRate: 0.01,
                    tags: {
                        eventType: eventQuest.eventType,
                        tier: eventQuest.tier,
                    },
                    fingerprint: 'QuestMenuHandler:onCreate',
                });
                return;
            }
            const targetRewards = rewards[index] as IRewardData[];
            giftText.parent.on(GameObjectEvent.SELECTED, () => {
                onGiftClicked(layout, targetRewards);
            });
            cleanupManager.add(() => {
                giftText.parent.off(GameObjectEvent.SELECTED, () => {
                    onGiftClicked(layout, targetRewards);
                });
            });
        });
    }

    // Set timer
    const timerTextComponent = layout.eventSubLayout.timeText.getComponent(TextComponent);
    timerTextComponent.text = DateHelper.timeTo(DateHelper.now, eventQuest.expiration, 'DD:hh:mm:ss');

    const timerInterval = Game.ticker.setInterval(() => {
        if (!timerTextComponent?.gameObject || timerTextComponent.gameObject.destroyed) {
            return;
        }
        timerTextComponent.text = DateHelper.timeTo(DateHelper.now, eventQuest.expiration, 'DD:hh:mm:ss');
        if ((eventQuest.expiration.getTime() - DateHelper.now.getTime()) < 1000)
            UIActions.hidePopup(PopupID.QUEST_MENU);
    }, 1000);

    cleanupManager.add(() => {
        Game.ticker.clearInterval(timerInterval);
    });

    setEventQuestProgress(state, layout);
}
